import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Link, useLocation } from 'react-router-dom';

import { cn } from '@workspace/ui/lib/utils';

import {
  ChevronRightIcon,
  CloseIcon,
  HeartIcon,
} from '~/components/MarketplaceIcons';
import { ProductListAnalyticsProvider } from '~/components/ProductListAnalytics';
import { useMounted } from '~/hooks/useMounted';
import { favoritesProductList } from '~/services/analytics/productLists';
import { userStore } from '~/stores';
import type { Product } from '~/stores/CategoriesStore/Product';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';

import { HEADER_BADGE_CLASS, HEADER_ICON_CLASS } from './constants';
import { HeaderDubaiExpressFavRow } from './HeaderDubaiExpressFavRow';

const POPOVER_ID = 'header-favorites';
const SKELETON_ROWS = 3;

/**
 * Favorites of the DubaiExpress header (desktop): a heart with the count and
 * a popover listing the saved products as the backend orders them, newest
 * first. The list is fetched when the popover opens and frozen while it is
 * open, so products removed there stay in place, dimmed, until it closes and
 * a slip can be undone. Guests do not get the entry, as in the rest of the
 * storefront, and it appears only after mount, since the server renders for
 * a guest.
 */
const HeaderDubaiExpressFavsView = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const mounted = useMounted();
  const [isOpen, setIsOpen] = useState(false);
  const [listed, setListed] = useState<Product[]>([]);

  const count = favoritesStore.favoritesIds.length;
  const isLoading = favoritesStore.favoritesStoreCRUDMeta.isLoading;

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (isOpen && !isLoading) {
      setListed([...favoritesStore.favorites]);
    }
  }, [isOpen, isLoading]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!mounted || !userStore.isAuthorized) {
    return null;
  }

  const close = () => setIsOpen(false);

  const toggle = () => {
    if (!isOpen) {
      setListed([]);
      favoritesStore.fetchFavorites(true);
    }
    setIsOpen(!isOpen);
  };

  return (
    <span className="relative flex">
      <button
        type="button"
        aria-label={t('favoritesDE:title')}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={POPOVER_ID}
        onClick={toggle}
        className={cn(
          HEADER_ICON_CLASS,
          'border-0 bg-transparent p-0 [font-family:inherit]',
        )}
      >
        <HeartIcon className="size-[22px]" />
        {count > 0 && <span className={HEADER_BADGE_CLASS}>{count}</span>}
      </button>
      {isOpen && (
        <>
          <span aria-hidden onClick={close} className="fixed inset-0 z-40" />
          <div
            id={POPOVER_ID}
            className="absolute top-[calc(100%+16px)] -right-4 z-[41] flex w-[380px] flex-col gap-2 rounded-[16px] border border-[#EDEDED] border-solid bg-white p-4 text-[#141415] shadow-[0_24px_48px_-16px_rgba(10,10,10,0.18)]"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-semibold text-[16px] leading-[22px]">
                {t('favoritesDE:title')}{' '}
                <span className="font-normal text-[#8A8A8A] text-[12px] leading-4">
                  {t('phrases:itemsCount', { count })}
                </span>
              </span>
              <button
                type="button"
                aria-label={t('close')}
                onClick={close}
                className="flex cursor-pointer border-0 bg-transparent p-0 text-[#8A8A8A] transition-colors duration-[120ms] hover:text-[#141415]"
              >
                <CloseIcon className="size-[18px]" />
              </button>
            </div>
            {isLoading ? (
              <div className="py-1" aria-busy>
                <Skeleton count={SKELETON_ROWS} height={56} borderRadius={10} />
              </div>
            ) : listed.length === 0 ? (
              <div className="pt-4 pb-2 text-[#5C5C5C] text-[14px] leading-5">
                {t('favoritesDE:empty')}
              </div>
            ) : (
              <ProductListAnalyticsProvider list={favoritesProductList()}>
                <div className="-mx-3 max-h-[352px] overflow-y-auto px-3 py-1 [scrollbar-width:none]">
                  {listed.map((product) => (
                    <HeaderDubaiExpressFavRow
                      key={product.id}
                      product={product}
                      onClose={close}
                    />
                  ))}
                </div>
              </ProductListAnalyticsProvider>
            )}
            <Link
              to="/favs"
              className="no-underline! mt-1 flex h-11 items-center justify-center gap-1.5 rounded-[8px] bg-[#E94E1A] font-semibold text-[14px] text-white! leading-none transition-colors duration-[120ms] hover:bg-[#C43F12]"
            >
              {t('favoritesDE:all')}
              <ChevronRightIcon className="size-4" />
            </Link>
          </div>
        </>
      )}
    </span>
  );
};

export const HeaderDubaiExpressFavs = observer(HeaderDubaiExpressFavsView);
